import { supabase } from './supabase';
import { User } from '@supabase/supabase-js';
import { checkAuthRateLimit, resetAuthRateLimit } from './rate-limiter';

export async function getCurrentUser(): Promise<User | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

export async function signIn(email: string, password: string): Promise<{
  success: boolean;
  user?: User;
  error?: string;
  rateLimited?: boolean;
  blockedUntil?: number;
}> {
  const rateLimitCheck = checkAuthRateLimit(email);

  if (!rateLimitCheck.allowed) {
    return {
      success: false,
      rateLimited: true,
      blockedUntil: rateLimitCheck.blockedUntil,
      error: `Too many failed login attempts. Please try again later.`
    };
  }

  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    if (data.user) {
      resetAuthRateLimit(email);
      return {
        success: true,
        user: data.user
      };
    }

    return {
      success: false,
      error: 'Authentication failed'
    };
  } catch (error) {
    console.error('Sign in error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Authentication failed'
    };
  }
}

export async function signOut() {
  try {
    if (supabase.realtime && supabase.realtime.channels) {
      supabase.realtime.channels.forEach(channel => {
        channel.unsubscribe();
      });
    }

    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    localStorage.removeItem('supabase.auth.token');

    return true;
  } catch (error) {
    console.error('Error signing out:', error);
    return false;
  }
}

export function onAuthStateChange(callback: (user: User | null) => void) {
  return supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user ?? null);
  }).data.subscription.unsubscribe;
}